import type { PropInstance } from "../document/types.ts";
import { artHeight, artLeft, artTop, artWidth } from "./art-box.ts";
import type { FrameSize } from "./prop-hit.ts";

/**
 * A cone of sight on the ground plane (ADR-061's amendment for P1.9-T75): a circular sector whose apex is a
 * character's feet point, whose axis is the way it looks, and whose angle and reach are the game's. Props whose art
 * meets it fade partly, so a player sees ahead through a forest. World pixels, y down the screen, headings in radians
 * from +x toward +y. The game decides the numbers; this module only measures.
 */

/**
 * The arc, in radians, within which an easing heading snaps to its target and comes to rest.
 */
export const HEADING_REST = 0.001;

/**
 * Slack, in pixels, by which a point on an edge of the sector counts as inside despite rounding.
 */
const EDGE_SLACK = 1e-9;

/**
 * A sector the caller keeps and refills each frame with `aimCone`, so aiming allocates nothing. `dirX`, `dirY` is the
 * unit axis, `cosHalf` the cosine of half the angle, `reach` the radius in pixels, and `leftX`, `leftY` and `rightX`,
 * `rightY` its two edges from the apex to the arc, as offsets in pixels.
 */
export interface SightCone {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  cosHalf: number;
  reach: number;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
}

/**
 * Where a prop stands and how it is mirrored: all the geometry the art test reads from it.
 */
type Placement = Pick<PropInstance, "x" | "y" | "flipX" | "flipY">;

/**
 * How props fade in a cone: the least height a prop's art must rise above its base before it may fade at all, the
 * alpha a prop in the cone eases to, and the time in ms the whole way from 1 to that alpha takes.
 */
export interface ConeFadeRule {
  readonly minHeight: number;
  readonly alpha: number;
  readonly fadeMs: number;
}

/**
 * A cone with no reach at the origin: it holds its apex alone until aimed.
 *
 * @returns The cone.
 */
export function newSightCone(): SightCone {
  return { x: 0, y: 0, dirX: 0, dirY: 1, cosHalf: 1, reach: 0, leftX: 0, leftY: 0, rightX: 0, rightY: 0 };
}

/**
 * Aims a cone: refills it with an apex, a heading, a full angle and a reach.
 *
 * @param cone The cone to refill.
 * @param x Apex x in world pixels.
 * @param y Apex y in world pixels.
 * @param heading The axis in radians.
 * @param angle The full angle in radians, in `(0, PI]`.
 * @param reach The radius in world pixels, at least 0.
 * @throws RangeError when the angle or the reach is out of range.
 */
export function aimCone(cone: SightCone, x: number, y: number, heading: number, angle: number, reach: number): void {
  if (!(angle > 0 && angle <= Math.PI)) {
    throw new RangeError(`a sight cone's angle must lie in (0, PI], not ${angle}`);
  }
  if (!(reach >= 0)) {
    throw new RangeError(`a sight cone's reach must be at least 0, not ${reach}`);
  }
  cone.x = x;
  cone.y = y;
  cone.dirX = Math.cos(heading);
  cone.dirY = Math.sin(heading);
  cone.cosHalf = Math.cos(angle / 2);
  cone.reach = reach;
  cone.leftX = Math.cos(heading - angle / 2) * reach;
  cone.leftY = Math.sin(heading - angle / 2) * reach;
  cone.rightX = Math.cos(heading + angle / 2) * reach;
  cone.rightY = Math.sin(heading + angle / 2) * reach;
}

/**
 * Whether a point lies inside a cone: the apex, or within the reach and no further from the axis than half the angle.
 * A point on an edge or on the arc is inside.
 *
 * @param cone The cone.
 * @param px Point x in world pixels.
 * @param py Point y in world pixels.
 * @returns True when the cone holds the point.
 */
export function inCone(cone: SightCone, px: number, py: number): boolean {
  const dx = px - cone.x;
  const dy = py - cone.y;
  const squared = dx * dx + dy * dy;
  if (squared === 0) {
    return true;
  }
  if (squared > cone.reach * cone.reach) {
    return false;
  }
  return dx * cone.dirX + dy * cone.dirY + EDGE_SLACK >= cone.cosHalf * Math.sqrt(squared);
}

/**
 * Whether a box meets a cone: some point of the box lies inside it. For a cone no wider than a half plane this holds
 * exactly when the box's point nearest the apex lies inside the cone, or one of the cone's two edges crosses the box:
 * were neither so, the segment from that nearest point to a shared point would cross an edge inside the box and within
 * the reach. A box wholly beyond the reach is rejected first, by one squared distance.
 *
 * @param cone The cone.
 * @param left The box's left edge in world pixels.
 * @param top Its top edge.
 * @param right Its right edge, at least `left`.
 * @param bottom Its bottom edge, at least `top`.
 * @returns True when the box and the cone share a point.
 */
export function boxMeetsCone(cone: SightCone, left: number, top: number, right: number, bottom: number): boolean {
  const nearX = Math.min(Math.max(cone.x, left), right);
  const nearY = Math.min(Math.max(cone.y, top), bottom);
  const dx = nearX - cone.x;
  const dy = nearY - cone.y;
  if (dx * dx + dy * dy > cone.reach * cone.reach) {
    return false;
  }
  return inCone(cone, nearX, nearY) || segmentMeetsBox(cone.x, cone.y, cone.leftX, cone.leftY, left, top, right, bottom) || segmentMeetsBox(cone.x, cone.y, cone.rightX, cone.rightY, left, top, right, bottom);
}

/**
 * Whether a prop's art meets a cone: its art bounds above its base, mirrored with its flips, as `propArtOverlap`
 * places them; without art bounds the whole frame width above the base stands in.
 *
 * @param prop The prop's anchor and flips.
 * @param size Its frame.
 * @param tileSize Tile size in pixels.
 * @param cone The cone.
 * @returns True when the art bounds and the cone share a point.
 */
export function propArtMeetsCone(prop: Placement, size: FrameSize, tileSize: number, cone: SightCone): boolean {
  const left = artLeft(prop, size, tileSize);
  const top = artTop(prop, size, tileSize);
  return boxMeetsCone(cone, left, top, left + artWidth(size), top + artHeight(size));
}

/**
 * Whether a segment from a point along an offset meets a box, by clipping its parameter to each axis's slab.
 *
 * @param px The segment's start x.
 * @param py Its start y.
 * @param dx Its offset along x.
 * @param dy Its offset along y.
 * @param left The box's left edge.
 * @param top Its top edge.
 * @param right Its right edge.
 * @param bottom Its bottom edge.
 * @returns True when they share a point.
 */
function segmentMeetsBox(px: number, py: number, dx: number, dy: number, left: number, top: number, right: number, bottom: number): boolean {
  let from = 0;
  let to = 1;
  if (dx === 0) {
    if (px < left || px > right) {
      return false;
    }
  } else {
    const a = (left - px) / dx;
    const b = (right - px) / dx;
    from = Math.max(from, Math.min(a, b));
    to = Math.min(to, Math.max(a, b));
  }
  if (dy === 0) {
    if (py < top || py > bottom) {
      return false;
    }
  } else {
    const a = (top - py) / dy;
    const b = (bottom - py) / dy;
    from = Math.max(from, Math.min(a, b));
    to = Math.min(to, Math.max(a, b));
  }
  return from <= to;
}

/**
 * A heading eased toward a target along the shorter arc: the gap closes by `1 - exp(-elapsed / turnMs)` of itself, and
 * once it is within `HEADING_REST` the heading is the target, so it comes to rest. Turning by keys from one side to the
 * other therefore sweeps through the way between instead of jumping.
 *
 * @param from The heading now, in radians.
 * @param to The target heading, in radians.
 * @param elapsedMs Time since the last ease.
 * @param turnMs The time constant; 0 snaps.
 * @returns The eased heading, in `(-PI, PI]`.
 */
export function easeHeading(from: number, to: number, elapsedMs: number, turnMs: number): number {
  const gap = wrapAngle(to - from);
  if (Math.abs(gap) <= HEADING_REST || turnMs <= 0) {
    return wrapAngle(to);
  }
  if (elapsedMs <= 0) {
    return from;
  }
  const next = from + gap * (1 - Math.exp(-elapsedMs / turnMs));
  return Math.abs(wrapAngle(to - next)) <= HEADING_REST ? wrapAngle(to) : wrapAngle(next);
}

/**
 * An angle brought into `(-PI, PI]`.
 *
 * @param angle The angle in radians.
 * @returns The same direction in `(-PI, PI]`.
 */
function wrapAngle(angle: number): number {
  const wrapped = Math.atan2(Math.sin(angle), Math.cos(angle));
  return wrapped === -Math.PI ? Math.PI : wrapped;
}
